const isFiniteTimestamp = (value) => Number.isFinite(value) && value >= 0;
const isNavigationRevision = (value) => Number.isInteger(value) && value > 0;
const isMeasuredRoute = ({ readiness, version }) => (
  version === "v1" ||
  (version === "v2" && (
    readiness === "candidate" || readiness === "parity-approved"
  ))
);
const ignored = (reason) => Object.freeze({ status: "ignored", reason });
const elapsed = (now, startedAt) => Math.max(0, Math.round(now() - startedAt));

const safelyRecord = (record, event) => {
  try {
    const pending = record(event);
    if (pending && typeof pending.catch === "function") pending.catch(() => {});
  } catch (_) {
    // Pilot measurement is deliberately best-effort and never blocks a view.
  }
};

/**
 * Owns the first stable feature measurement for one navigation revision.
 *
 * Callers begin a V1 baseline, parity-approved V2, or preview candidate V2
 * attempt, then report ready. Browser lifecycle adapters report page exit.
 * The module owns stale-attempt rejection and exactly-once settlement;
 * navigation revisions and readiness never enter telemetry.
 */
export const createPilotFeatureLifecycle = ({ now, record }) => {
  if (typeof now !== "function") throw new TypeError("now must be a function");
  if (typeof record !== "function") throw new TypeError("record must be a function");

  let activeAttempt = null;
  let latestSettledRevision = 0;

  const settle = (eventForAttempt) => {
    if (!activeAttempt) return ignored("inactive");
    const attempt = activeAttempt;
    activeAttempt = null;
    latestSettledRevision = Math.max(
      latestSettledRevision,
      attempt.navigationRevision
    );
    safelyRecord(record, eventForAttempt(attempt));
    return Object.freeze({
      status: "reported",
      navigationRevision: attempt.navigationRevision,
    });
  };

  const begin = ({
    destinationId,
    navigationRevision,
    readiness,
    startedAt,
    version,
  } = {}) => {
    if (!isNavigationRevision(navigationRevision)) return ignored("invalid-attempt");
    if (navigationRevision <= latestSettledRevision) {
      return ignored("stale-revision");
    }
    if (
      activeAttempt &&
      navigationRevision < activeAttempt.navigationRevision
    ) {
      return ignored("stale-revision");
    }
    if (activeAttempt?.navigationRevision === navigationRevision) {
      return Object.freeze({ status: "tracking", navigationRevision });
    }
    if (
      activeAttempt &&
      navigationRevision > activeAttempt.navigationRevision
    ) {
      settle((attempt) => ({
        type: "abandon",
        destinationId: attempt.destinationId,
        version: attempt.version,
        durationMs: elapsed(now, attempt.startedAt),
        phase: "navigation-superseded-before-feature-stable",
      }));
    }
    if (!isMeasuredRoute({ readiness, version })) {
      return ignored("not-measured-route");
    }
    if (
      typeof destinationId !== "string" || destinationId.trim() === "" ||
      !isFiniteTimestamp(startedAt)
    ) {
      return ignored("invalid-attempt");
    }

    activeAttempt = {
      destinationId: destinationId.trim(),
      navigationRevision,
      startedAt,
      version,
    };
    return Object.freeze({ status: "tracking", navigationRevision });
  };

  const reportReady = ({ navigationRevision } = {}) => {
    if (!isNavigationRevision(navigationRevision)) return ignored("invalid-revision");
    if (!activeAttempt) {
      return navigationRevision <= latestSettledRevision
        ? ignored("already-settled")
        : ignored("inactive");
    }
    if (activeAttempt.navigationRevision !== navigationRevision) {
      return ignored("stale-revision");
    }

    return settle((attempt) => ({
      type: "render",
      destinationId: attempt.destinationId,
      version: attempt.version,
      durationMs: elapsed(now, attempt.startedAt),
      phase: "feature-stable",
    }));
  };

  const reportPageExit = ({ phase } = {}) => {
    if (phase !== "pagehide" && phase !== "visibility-hidden") {
      return ignored("invalid-phase");
    }
    return settle((attempt) => ({
      type: "abandon",
      destinationId: attempt.destinationId,
      version: attempt.version,
      durationMs: elapsed(now, attempt.startedAt),
      phase: `${phase}-before-feature-stable`,
    }));
  };

  return Object.freeze({ begin, reportPageExit, reportReady });
};

/**
 * Browser adapter for page abandonment. It deliberately exposes no location,
 * URL or document content to the lifecycle module.
 */
export const observePilotPageExit = ({ documentObject, onPageExit, windowObject }) => {
  if (!windowObject || typeof windowObject.addEventListener !== "function") {
    return () => {};
  }
  if (typeof onPageExit !== "function") {
    throw new TypeError("onPageExit must be a function");
  }

  const handlePageHide = () => onPageExit({ phase: "pagehide" });
  const handleVisibilityChange = () => {
    if (documentObject?.visibilityState === "hidden") {
      onPageExit({ phase: "visibility-hidden" });
    }
  };

  windowObject.addEventListener("pagehide", handlePageHide);
  documentObject?.addEventListener?.("visibilitychange", handleVisibilityChange);

  return () => {
    windowObject.removeEventListener?.("pagehide", handlePageHide);
    documentObject?.removeEventListener?.("visibilitychange", handleVisibilityChange);
  };
};
